// D02 Unit 04 — Native VLAN and Allowed VLAN Lists (LAB: BUILD → BREAK → FIX → VERIFY)
import { note, readConfirm } from '../../ui/widgets.js';
import { twoSwitch, twoSwitchLayout } from './topologies.js';
import { ran, pinged, reach, isTrunk } from './helpers.js';

export default {
  id: 'u04', num: '04', kind: 'LAB', title: 'Native VLAN and Allowed VLAN Lists', type: 'Configuration Lab', implemented: true,
  objective: 'Restrict a trunk to the VLANs it should carry, then find and fix a VLAN that went missing from the allowed list.',
  network: () => twoSwitch({ allowed: null }),
  layout: { ...twoSwitchLayout, linkNotes: { 'SW1:GigabitEthernet0/1--SW2:GigabitEthernet0/1': '802.1Q · native 99 · allowed: ALL (to harden)' } },
  linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'What a trunk is allowed to carry', workspace: false,
      content: `<p>By default a trunk carries VLANs 1–4094. That spreads broadcasts and unknown VLANs everywhere. Restrict each trunk to what it needs.</p>
<table><tr><th>Command</th><th>Effect</th></tr>
<tr><td><code>switchport trunk allowed vlan 10,20,99</code></td><td>Replaces the list</td></tr>
<tr><td><code>switchport trunk allowed vlan add 30</code></td><td>Adds to the list</td></tr>
<tr><td><code>switchport trunk allowed vlan remove 20</code></td><td>Removes from the list</td></tr></table>
<div class="callout warn">Typing <code>allowed vlan 30</code> when you meant <code>allowed vlan add 30</code> silently removes every other VLAN. It is one of the most common causes of outages on trunks.</div>`,
      widgets: [readConfirm({ id: 'd2u04-read' })],
    },
    {
      id: 'build', phase: 'BUILD', title: 'Harden the trunk', devices: ['SW1', 'SW2'],
      content: '<p>Restrict Gi0/1 on both switches to VLANs <b>10, 20, 99</b>, then check <code>show interfaces trunk</code> on both.</p>',
      evidence: [
        { id: 't1', text: 'SW1: trunk shows allowed 10,20,99', match: ran('SW1', 'show interfaces trunk', /Vlans allowed on trunk\nGi0\/1\s+10,20,99/) },
        { id: 't2', text: 'SW2: trunk shows allowed 10,20,99', match: ran('SW2', 'show interfaces trunk', /Vlans allowed on trunk\nGi0\/1\s+10,20,99/) },
      ],
      tasks: ['SW1', 'SW2'].map((s) => ({ id: s, text: `${s} Gi0/1 allows exactly 10,20,99, native 99`, check: (rt) => isTrunk(rt, s, 'GigabitEthernet0/1', { native: 99, allowed: [10, 20, 99] }) && rt.net.devices[s].interfaces['GigabitEthernet0/1'].allowed?.length === 3, fail: `${s} Gi0/1 allowed list is not 10,20,99.` })),
      hints: ['<code>interface gi0/1</code> → <code>switchport trunk allowed vlan 10,20,99</code>.'],
    },
    {
      id: 'break', phase: 'BREAK → TROUBLESHOOT → FIX', title: 'VLAN 20 stops at the trunk', devices: ['SW1', 'SW2', 'PC2'],
      onEnter: (rt) => { rt.net.devices.SW2.interfaces['GigabitEthernet0/1'].allowed = [10, 99]; },
      ticket: { id: '2004', priority: 2, from: 'Support team', subject: 'Support on floor 2 cannot reach Support on floor 3', body: 'Users in VLAN 20 on SW1 cannot reach VLAN 20 users on SW2. Sales is fine. A colleague "tidied" a trunk during lunch.' },
      evidence: [
        { id: 'repro', text: 'PC2: test to PC4 run', match: pinged('PC2', '10.10.20.21') },
        { id: 't1', text: 'SW1: trunk checked', match: ran('SW1', 'show interfaces trunk') },
        { id: 't2', text: 'SW2: trunk checked', match: ran('SW2', 'show interfaces trunk') },
      ],
      tasks: [
        { id: 'fix', text: 'VLAN 20 allowed on both ends of the trunk', check: (rt) => isTrunk(rt, 'SW1', 'GigabitEthernet0/1', { allowed: [10, 20, 99] }) && isTrunk(rt, 'SW2', 'GigabitEthernet0/1', { allowed: [10, 20, 99] }), fail: 'VLAN 20 is still missing from a trunk.' },
        { id: 'e2e', text: 'PC2 reaches PC4 and Sales still works', check: (rt) => reach(rt, 'PC2', '10.10.20.21') && reach(rt, 'PC1', '10.10.10.21'), fail: 'End-to-end test failed.' },
      ],
      question: 'Sales still works over the same cable. What does that tell you about the physical layer?',
      hints: ['Compare the "Vlans allowed on trunk" section on both switches.', 'Use <code>switchport trunk allowed vlan add 20</code> — not the replace form.'],
    },
    {
      id: 'verify', phase: 'VERIFY → DOCUMENT', title: 'Verify and document', devices: ['SW2', 'PC2'],
      evidence: [
        { id: 'ping', text: 'PC2: 4/4 replies from PC4', match: pinged('PC2', '10.10.20.21', { success: true, all: true }) },
        { id: 'fwd', text: 'SW2: VLAN 20 forwarding on the trunk', match: ran('SW2', 'show interfaces trunk', /not pruned\nGi0\/1\s+10,20,99/) },
      ],
      widgets: [note({ id: 'd2u04-close', title: 'TICKET CLOSURE NOTE', prompt: 'Root cause, fix, and how to avoid it next time.', min: 40 })],
      verifyLabel: 'Verify & close ticket',
    },
  ],
};
